import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { fetchData } from 'utils';
import { useClientConstraintsQuery } from 'hooks/webapi/application/useUpsale';
import {
  useAffiliate,
  useClientQuery,
  useExternalConsentsDataQuery,
  usePrecheckApplicationMutation,
} from 'hooks/webapi';
import useCalculator from 'hooks/calculator/useCalculator';
import { WAITING } from 'Router/routes';
import {
  CreateApplicationOptions,
  useCreateApplicationWithoutRiskAndSendGACookiesMutation,
} from 'hooks/webapi/client/useCreateApplicationMutation';
import useIovation from 'hooks/useIovation';

export default function useWelcomeBackNewApplication() {
  const navigate = useNavigate();
  const { data: constraints } = useClientConstraintsQuery();
  const { data: client, refetch: refetchClient } = useClientQuery();
  const { amount, handleAmountChange, offer, loading } = useCalculator();
  const { mutateAsync: createClientApplicationWithoutRisk } =
    useCreateApplicationWithoutRiskAndSendGACookiesMutation();
  const { affiliate, removeAffiliateData } = useAffiliate();
  const [pending, setPending] = useState(false);
  const { ioBlackBox } = useIovation();
  const { mutateAsync: precheckApplication } = usePrecheckApplicationMutation();
  const { data: consents, isLoading: consentsLoading } =
    useExternalConsentsDataQuery();

  useEffect(() => {
    if (!client) {
      refetchClient();
    }
  }, [client, refetchClient]);

  useEffect(() => {
    if (!constraints) {
      return;
    }

    handleAmountChange(constraints.amountInterval.max);
  }, [constraints, handleAmountChange]);

  const consentTelco = consents?.UNDERSTANDING_AGREEMENT_CONFIRMED;
  const consent = consentTelco?.content[0].text;

  const acceptTelcoConsent = async () => {
    const consentPayload = {
      consents: [
        {
          name: 'UNDERSTANDING_AGREEMENT_CONFIRMED',
          accepted: true,
          version: consentTelco?.version,
        },
      ],
    };
    await fetchData('/client/settings', {
      method: 'put',
      body: JSON.stringify(consentPayload),
    });
  };

  const handleConfirm = useCallback(async () => {
    if (!offer) {
      return;
    }

    setPending(true);

    const applicationOptions: CreateApplicationOptions = {
      amount: offer.newPrincipal,
      term: offer.term,
      type: 'WEB',
      source: 'DESKTOP',
      affiliateInfo: affiliate,
      ioBlackBox,
    };
    await acceptTelcoConsent();
    await createClientApplicationWithoutRisk(applicationOptions);
    await precheckApplication();
    if (affiliate) {
      removeAffiliateData();
    }

    setPending(false);
    navigate(WAITING);
  }, [
    affiliate,
    createClientApplicationWithoutRisk,
    ioBlackBox,
    offer,
    precheckApplication,
    removeAffiliateData,
  ]);

  return {
    client,
    constraints,
    amount,
    loading,
    pending,
    offer,
    handleConfirm,
    handleAmountChange,
    consent,
    consentsLoading,
  };
}
